import { useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { collaboratorAllocationsQueryKeys } from './useCollaboratorAllocationsQuery';
import { AllocationStatuses } from '@/features/Collaborators/Models';
import { useAuth } from '@/contexts/AuthContext';

export interface CancelCollaboratorAllocationVariables {
  collaboratorId: string;
  allocationId: string;
  reason?: string | null;
}

function sanitizeReason(input?: string | null): string | null {
  if (!input) {
    return null;
  }

  const trimmed = input.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function useCancelCollaboratorAllocationMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ collaboratorId, allocationId, reason }: CancelCollaboratorAllocationVariables) => {
      if (!collaboratorId) {
        throw new Error('Colaborador não informado.');
      }

      if (!allocationId) {
        throw new Error('Alocação não informada.');
      }

      if (!user) {
        throw new Error('Usuário não autenticado.');
      }

      const now = Timestamp.now();
      const payload = {
        status: AllocationStatuses.Cancelled,
        cancelReason: sanitizeReason(reason),
        canceledAt: now,
        canceledById: user.uid,
        canceledByName: user.displayName ?? user.email ?? 'Usuário Raro',
        canceledByAvatar: user.photoURL ?? null,
        updatedAt: now,
        updatedById: user.uid,
        updatedByName: user.displayName ?? user.email ?? 'Usuário Raro',
        updatedByAvatar: user.photoURL ?? null,
      };

      const allocationRef = doc(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations',
        allocationId
      );

      await updateDoc(allocationRef, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: collaboratorAllocationsQueryKeys.list(variables.collaboratorId),
      });
      queryClient.invalidateQueries({
        queryKey: collaboratorAllocationsQueryKeys.detail(
          variables.collaboratorId,
          variables.allocationId
        ),
      });
    },
  });
}

export type UseCancelCollaboratorAllocationMutationResult = ReturnType<
  typeof useCancelCollaboratorAllocationMutation
>;
